import React, { useState, useEffect } from 'react';
import { 
  Calendar, Pill, AlertCircle, CheckCircle, Clock, TrendingUp, Heart, Settings, 
  Zap, Droplet, Wind, Activity, Moon, Sun, Bell, Share2, Download, BarChart3, 
  LineChart, PieChart, X, Plus, Filter, Search, ChevronDown, TrendingDown, Users,
  Target, Flame, Brain, Smile, Frown, Coffee, Utensils, Dumbbell, Book
} from 'lucide-react';

export default function ChemoAnalytics({ data }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [theme, setTheme] = useState('dark');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);

  // Chart data for effects over time — live data when provided, demo otherwise
  const weeklyEffectsData = data?.weeklyEffects || [
    { week: 'Week 1', count: 5, severity: 8 },
    { week: 'Week 2', count: 12, severity: 18 },
    { week: 'Week 3', count: 18, severity: 28 },
    { week: 'Week 4', count: 15, severity: 24 },
  ];

  // Daily mood and wellness data
  const dailyMoodData = data?.dailyMood || [
    { day: 'Mon', mood: 6, energy: 5, nausea: 4, pain: 2 },
    { day: 'Tue', mood: 5, energy: 4, nausea: 6, pain: 3 },
    { day: 'Wed', mood: 4, energy: 3, nausea: 7, pain: 4 },
    { day: 'Thu', mood: 5, energy: 4, nausea: 5, pain: 3 },
    { day: 'Fri', mood: 7, energy: 6, nausea: 3, pain: 2 },
    { day: 'Sat', mood: 8, energy: 7, nausea: 2, pain: 1 },
    { day: 'Sun', mood: 7, energy: 6, nausea: 3, pain: 2 },
  ];

  // Effect distribution pie chart data
  const effectDistribution = data?.effectDistribution || [
    { category: 'Gastrointestinal', percentage: 35, count: 11, color: 'from-orange-500 to-orange-600' },
    { category: 'Systemic', percentage: 25, count: 8, color: 'from-blue-500 to-blue-600' },
    { category: 'Psychological', percentage: 20, count: 6, color: 'from-purple-500 to-purple-600' },
    { category: 'Dermatological', percentage: 12, count: 4, color: 'from-pink-500 to-pink-600' },
    { category: 'Other', percentage: 8, count: 3, color: 'from-slate-500 to-slate-600' },
  ];

  // Recovery timeline
  const recoveryMilestones = data?.recoveryMilestones || [
    { week: 1, milestone: 'First Chemo Session', status: 'completed', details: 'Tolerated well', date: 'May 15' },
    { week: 2, milestone: 'Initial Side Effects', status: 'completed', details: 'Peak symptoms', date: 'May 22' },
    { week: 3, milestone: 'Mid-cycle Check-in', status: 'in-progress', details: 'Adjusting treatment', date: 'May 29' },
    { week: 4, milestone: 'Recovery Phase Begins', status: 'upcoming', details: 'Expected improvement', date: 'Jun 5' },
  ];

  // Medication adherence
  const medicationData = data?.medicationAdherence || [
    { name: 'Doxorubicin', doses: 4, taken: 4, percentage: 100, color: 'from-emerald-500 to-emerald-600' },
    { name: 'Cyclophosphamide', doses: 4, taken: 4, percentage: 100, color: 'from-blue-500 to-blue-600' },
    { name: 'Anti-nausea Meds', doses: 28, taken: 26, percentage: 93, color: 'from-yellow-500 to-yellow-600' },
    { name: 'Pain Management', doses: 21, taken: 19, percentage: 90, color: 'from-red-500 to-red-600' },
  ];

  // Wellness score components
  const wellnessComponents = data?.wellnessComponents || [
    { label: 'Physical Health', score: 65, icon: Activity, color: 'from-emerald-500 to-emerald-600' },
    { label: 'Mental Health', score: 58, icon: Brain, color: 'from-purple-500 to-purple-600' },
    { label: 'Emotional Balance', score: 62, icon: Heart, color: 'from-rose-500 to-rose-600' },
    { label: 'Sleep Quality', score: 54, icon: Moon, color: 'from-indigo-500 to-indigo-600' },
    { label: 'Nutrition', score: 71, icon: Utensils, color: 'from-orange-500 to-orange-600' },
    { label: 'Energy Levels', score: 48, icon: Zap, color: 'from-yellow-500 to-yellow-600' },
  ];

  const overallWellnessScore = data?.overallWellnessScore ?? Math.round(
    wellnessComponents.reduce((sum, comp) => sum + comp.score, 0) / wellnessComponents.length
  );

  // Key insights
  const insights = data?.insights || [
    { title: 'Peak Effect Time', value: 'Days 3-4', description: 'Strongest side effects occur mid-cycle', icon: '📍' },
    { title: 'Recovery Rate', value: '85%', description: 'Expected to improve by end of week', icon: '🚀' },
    { title: 'Compliance', value: '94%', description: 'Excellent medication adherence', icon: '✅' },
    { title: 'Energy Trend', value: '↓ 20%', description: 'Compared to last week', icon: '⚡' },
  ];

  const SimpleLineChart = ({ data, dataKey, strokeColor = '#10b981' }) => {
    const max = Math.max(...data.map(d => d[dataKey]));
    const height = 100;
    
    return (
      <svg width="100%" height={height} viewBox={`0 0 ${data.length * 50} ${height}`}>
        <polyline
          points={data.map((d, i) => `${i * 50 + 25},${height - (d[dataKey] / max) * (height - 20)}`).join(' ')}
          fill="none"
          stroke={strokeColor}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
        {data.map((d, i) => (
          <circle
            key={i}
            cx={i * 50 + 25}
            cy={height - (d[dataKey] / max) * (height - 20)}
            r="3"
            fill={strokeColor}
          />
        ))}
      </svg>
    );
  };

  const SimpleBarChart = ({ data, dataKey, color = '#10b981' }) => {
    const max = Math.max(...data.map(d => d[dataKey]));
    const barWidth = 35;
    const spacing = 50;
    
    return (
      <svg width="100%" height="150" viewBox={`0 0 ${data.length * spacing} 150`}>
        {data.map((d, i) => {
          const height = (d[dataKey] / max) * 100;
          return (
            <g key={i}>
              <rect
                x={i * spacing + 8}
                y={120 - height}
                width={barWidth}
                height={height}
                fill={color}
                opacity="0.7"
                rx="4"
              />
              <text
                x={i * spacing + 8 + barWidth / 2}
                y="140"
                fontSize="11"
                textAnchor="middle"
                fill={theme === 'dark' ? '#94a3b8' : '#64748b'}
              >
                {d.week || d.day}
              </text>
            </g>
          );
        })}
      </svg>
    );
  };

  const PieChartComponent = ({ data }) => {
    let currentAngle = 0;
    
    return (
      <svg width="100%" height="200" viewBox="0 0 200 200">
        {data.map((item, i) => {
          const angle = (item.percentage / 100) * 360;
          const startAngle = currentAngle;
          const endAngle = currentAngle + angle;
          const start = polarToCartesian(100, 100, 80, endAngle);
          const end = polarToCartesian(100, 100, 80, startAngle);
          const largeArc = angle > 180 ? 1 : 0;
          
          const pathData = [
            `M 100 100`,
            `L ${polarToCartesian(100, 100, 80, startAngle).x} ${polarToCartesian(100, 100, 80, startAngle).y}`,
            `A 80 80 0 ${largeArc} 1 ${end.x} ${end.y}`,
            'Z'
          ].join(' ');
          
          const result = (
            <path
              key={i}
              d={pathData}
              fill={getColorFromGradient(item.color)}
              opacity="0.8"
            />
          );
          
          currentAngle = endAngle;
          return result;
        })}
      </svg>
    );
  };

  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  const getColorFromGradient = (gradient) => {
    const colorMap = {
      'from-orange-500 to-orange-600': '#f97316',
      'from-blue-500 to-blue-600': '#3b82f6',
      'from-purple-500 to-purple-600': '#a855f7',
      'from-pink-500 to-pink-600': '#ec4899',
      'from-slate-500 to-slate-600': '#64748b',
      'from-emerald-500 to-emerald-600': '#10b981',
      'from-yellow-500 to-yellow-600': '#eab308',
      'from-red-500 to-red-600': '#ef4444',
      'from-indigo-500 to-indigo-600': '#6366f1',
    };
    return colorMap[gradient] || '#10b981';
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 ${theme === 'dark' 
      ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-50' 
      : 'bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50 text-slate-900'}`}>
      
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 opacity-5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-emerald-500 opacity-5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:60px_60px] opacity-30"></div>
      </div>

      {/* Header */}
      <header className="relative border-b border-slate-700/50 backdrop-blur-xl bg-slate-950/40 shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-600 shadow-lg">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-black bg-gradient-to-r from-emerald-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                  ChemoAnalytics
                </h1>
                <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                  Advanced Treatment Analytics & Insights
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button className={`p-3 rounded-lg transition-all ${theme === 'dark' ? 'hover:bg-slate-800/50' : 'hover:bg-slate-200/50'}`}>
                <Bell className="w-5 h-5" />
              </button>
              <button className={`p-3 rounded-lg transition-all ${theme === 'dark' ? 'hover:bg-slate-800/50' : 'hover:bg-slate-200/50'}`}>
                <Download className="w-5 h-5" />
              </button>
              <button
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className={`p-3 rounded-lg transition-all ${theme === 'dark' ? 'hover:bg-slate-800/50 text-yellow-300' : 'hover:bg-slate-200/50 text-blue-600'}`}>
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button className={`p-3 rounded-lg transition-all ${theme === 'dark' ? 'hover:bg-slate-800/50' : 'hover:bg-slate-200/50'}`}>
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {[
              { id: 'overview', label: '📊 Overview', icon: BarChart3 },
              { id: 'analytics', label: '📈 Analytics', icon: LineChart },
              { id: 'wellness', label: '💚 Wellness', icon: Heart },
              { id: 'recovery', label: '🚀 Recovery', icon: TrendingUp },
              { id: 'insights', label: '💡 Insights', icon: Brain },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 rounded-lg font-bold whitespace-nowrap transition-all duration-300 ${
                  activeTab === tab.id
                    ? theme === 'dark'
                      ? 'bg-emerald-600/30 border border-emerald-500/50 text-emerald-300'
                      : 'bg-emerald-200/50 border border-emerald-400/50 text-emerald-700'
                    : theme === 'dark'
                      ? 'bg-slate-800/30 border border-slate-700/30 text-slate-400 hover:text-slate-300'
                      : 'bg-slate-200/30 border border-slate-300/30 text-slate-600 hover:text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative max-w-7xl mx-auto px-6 py-8">
        
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Overall Wellness Score */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className={`rounded-2xl backdrop-blur border overflow-hidden col-span-1 md:col-span-2 ${
                theme === 'dark'
                  ? 'bg-slate-800/40 border-slate-700/50'
                  : 'bg-white/40 border-slate-300/50'
              }`} style={{ animation: `slideUp 0.6s ease-out 0.1s both` }}>
                <div className="p-8">
                  <h2 className="text-2xl font-bold mb-6">Overall Wellness Score</h2>
                  
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    {wellnessComponents.map((comp, idx) => {
                      const Icon = comp.icon;
                      return (
                        <div
                          key={idx}
                          className={`rounded-lg p-4 cursor-pointer transition-all duration-300 hover:scale-105 ${
                            theme === 'dark'
                              ? 'bg-slate-700/30 hover:bg-slate-700/50'
                              : 'bg-slate-100/40 hover:bg-slate-100/60'
                          }`}
                          onClick={() => setSelectedMetric(comp)}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`p-2 rounded-lg bg-gradient-to-br ${comp.color} shadow-lg`}>
                              <Icon className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-xs font-bold text-slate-400">
                              {comp.score}%
                            </span>
                          </div>
                          <p className="text-xs font-semibold">{comp.label}</p>
                          
                          <div className={`h-2 rounded-full mt-2 overflow-hidden ${
                            theme === 'dark' ? 'bg-slate-600/30' : 'bg-slate-300/30'
                          }`}>
                            <div
                              className={`h-full bg-gradient-to-r ${comp.color} transition-all duration-500`}
                              style={{ width: `${comp.score}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between pt-6 border-t border-slate-700/30">
                    <div>
                      <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                        Current Overall Score
                      </p>
                      <p className="text-5xl font-black bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent">
                        {overallWellnessScore}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm mb-2 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                        Status
                      </p>
                      <div className={`px-4 py-2 rounded-lg font-bold ${
                        overallWellnessScore >= 70
                          ? theme === 'dark'
                            ? 'bg-emerald-500/20 text-emerald-300'
                            : 'bg-emerald-100 text-emerald-700'
                          : overallWellnessScore >= 50
                            ? theme === 'dark'
                              ? 'bg-yellow-500/20 text-yellow-300'
                              : 'bg-yellow-100 text-yellow-700'
                            : theme === 'dark'
                              ? 'bg-red-500/20 text-red-300'
                              : 'bg-red-100 text-red-700'
                      }`}>
                        {overallWellnessScore >= 70 ? '✅ Good' : overallWellnessScore >= 50 ? '⚠️ Fair' : '❌ Needs Attention'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="space-y-4" style={{ animation: `slideUp 0.6s ease-out 0.2s both` }}>
                {insights.map((insight, idx) => (
                  <div
                    key={idx}
                    className={`rounded-xl backdrop-blur border p-4 transition-all duration-300 hover:scale-105 ${
                      theme === 'dark'
                        ? 'bg-slate-800/40 border-slate-700/50 hover:border-slate-600/50'
                        : 'bg-white/40 border-slate-300/50 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{insight.icon}</span>
                      <div className="flex-1">
                        <p className={`text-xs font-semibold ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                          {insight.title}
                        </p>
                        <p className="text-xl font-black mb-1">{insight.value}</p>
                        <p className={`text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-slate-700'}`}>
                          {insight.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Effects Distribution */}
            <div className={`rounded-2xl backdrop-blur border p-8 ${
              theme === 'dark'
                ? 'bg-slate-800/40 border-slate-700/50'
                : 'bg-white/40 border-slate-300/50'
            }`} style={{ animation: `slideUp 0.6s ease-out 0.3s both` }}>
              <h3 className={`text-2xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Side Effects Distribution</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex items-center justify-center">
                  <PieChartComponent data={effectDistribution} />
                </div>
                
                <div className="space-y-3">
                  {effectDistribution.map((effect, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className={`w-4 h-4 rounded-full bg-gradient-to-r ${effect.color}`}></div>
                        <span className="font-semibold">{effect.category}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-lg">{effect.percentage}%</span>
                        <span className={`text-xs ml-2 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                          ({effect.count} effects)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && (
          <div className="space-y-8">
            {/* Effects Trend */}
            <div className={`rounded-2xl backdrop-blur border p-8 ${
              theme === 'dark'
                ? 'bg-slate-800/40 border-slate-700/50'
                : 'bg-white/40 border-slate-300/50'
            }`} style={{ animation: `slideUp 0.6s ease-out 0.1s both` }}>
              <h3 className="text-2xl font-bold mb-6">Effects Trend Over Time</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p className={`text-sm font-semibold mb-4 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                    Effect Count by Week
                  </p>
                  <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-slate-700/30' : 'bg-slate-100/40'}`}>
                    <SimpleBarChart data={weeklyEffectsData} dataKey="count" color="#10b981" />
                  </div>
                </div>
                
                <div>
                  <p className={`text-sm font-semibold mb-4 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                    Cumulative Severity Score
                  </p>
                  <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-slate-700/30' : 'bg-slate-100/40'}`}>
                    <SimpleBarChart data={weeklyEffectsData} dataKey="severity" color="#ef4444" />
                  </div>
                </div>
              </div>
            </div>

            {/* Daily Wellness Metrics */}
            <div className={`rounded-2xl backdrop-blur border p-8 ${
              theme === 'dark'
                ? 'bg-slate-800/40 border-slate-700/50'
                : 'bg-white/40 border-slate-300/50'
            }`} style={{ animation: `slideUp 0.6s ease-out 0.2s both` }}>
              <h3 className="text-2xl font-bold mb-6">Daily Wellness Patterns</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                    <span className="font-semibold">Mood Level</span>
                  </div>
                  <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-slate-700/30' : 'bg-slate-100/40'}`}>
                    <SimpleLineChart data={dailyMoodData} dataKey="mood" strokeColor="#10b981" />
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                    <span className="font-semibold">Nausea Level</span>
                  </div>
                  <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-slate-700/30' : 'bg-slate-100/40'}`}>
                    <SimpleLineChart data={dailyMoodData} dataKey="nausea" strokeColor="#f97316" />
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="font-semibold">Energy Level</span>
                  </div>
                  <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-slate-700/30' : 'bg-slate-100/40'}`}>
                    <SimpleLineChart data={dailyMoodData} dataKey="energy" strokeColor="#3b82f6" />
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className="font-semibold">Pain Level</span>
                  </div>
                  <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-slate-700/30' : 'bg-slate-100/40'}`}>
                    <SimpleLineChart data={dailyMoodData} dataKey="pain" strokeColor="#ef4444" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* WELLNESS TAB */}
        {activeTab === 'wellness' && (
          <div className="space-y-8">
            <div className={`rounded-2xl backdrop-blur border p-8 ${
              theme === 'dark'
                ? 'bg-slate-800/40 border-slate-700/50'
                : 'bg-white/40 border-slate-300/50'
            }`} style={{ animation: `slideUp 0.6s ease-out 0.1s both` }}>
              <h3 className="text-2xl font-bold mb-6">Medication Adherence</h3>
              
              <div className="space-y-4">
                {medicationData.map((med, idx) => (
                  <div key={idx} className={`rounded-lg p-4 border transition-all ${
                    theme === 'dark'
                      ? 'bg-slate-700/30 border-slate-600/30'
                      : 'bg-slate-100/40 border-slate-300/30'
                  }`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-bold text-lg">{med.name}</p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                          {med.taken} / {med.doses} doses taken
                        </p>
                      </div>
                      <div className={`px-4 py-2 rounded-lg font-bold ${
                        med.percentage === 100
                          ? theme === 'dark'
                            ? 'bg-emerald-500/20 text-emerald-300'
                            : 'bg-emerald-100 text-emerald-700'
                          : med.percentage >= 90
                            ? theme === 'dark'
                              ? 'bg-yellow-500/20 text-yellow-300'
                              : 'bg-yellow-100 text-yellow-700'
                            : theme === 'dark'
                              ? 'bg-red-500/20 text-red-300'
                              : 'bg-red-100 text-red-700'
                      }`}>
                        {med.percentage}%
                      </div>
                    </div>
                    
                    <div className={`h-3 rounded-full overflow-hidden ${
                      theme === 'dark' ? 'bg-slate-600/30' : 'bg-slate-300/30'
                    }`}>
                      <div
                        className={`h-full bg-gradient-to-r ${med.color} transition-all duration-500`}
                        style={{ width: `${med.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Wellness Recommendations */}
            <div className={`rounded-2xl backdrop-blur border p-8 ${
              theme === 'dark'
                ? 'bg-slate-800/40 border-slate-700/50'
                : 'bg-white/40 border-slate-300/50'
            }`} style={{ animation: `slideUp 0.6s ease-out 0.2s both` }}>
              <h3 className="text-2xl font-bold mb-6">Personalized Recommendations</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { icon: '💧', title: 'Hydration', desc: 'Increase water intake to 3L daily' },
                  { icon: '😴', title: 'Sleep', desc: 'Aim for 8 hours per night' },
                  { icon: '🥗', title: 'Nutrition', desc: 'Focus on protein-rich foods' },
                  { icon: '🧘', title: 'Mindfulness', desc: 'Practice 10min meditation daily' },
                  { icon: '🚶', title: 'Activity', desc: 'Light walking 20 minutes daily' },
                  { icon: '🤝', title: 'Support', desc: 'Join support group meetings' },
                ].map((rec, idx) => (
                  <div
                    key={idx}
                    className={`rounded-lg p-4 border transition-all duration-300 hover:scale-105 cursor-pointer ${
                      theme === 'dark'
                        ? 'bg-slate-700/30 border-slate-600/30 hover:border-slate-500/50'
                        : 'bg-slate-100/40 border-slate-300/30 hover:border-slate-300/60'
                    }`}
                  >
                    <p className="text-2xl mb-2">{rec.icon}</p>
                    <p className="font-bold mb-1">{rec.title}</p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                      {rec.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* RECOVERY TAB */}
        {activeTab === 'recovery' && (
          <div className={`rounded-2xl backdrop-blur border p-8 ${
            theme === 'dark'
              ? 'bg-slate-800/40 border-slate-700/50'
              : 'bg-white/40 border-slate-300/50'
          }`} style={{ animation: `slideUp 0.6s ease-out 0.1s both` }}>
            <h3 className="text-2xl font-bold mb-8">Recovery Timeline</h3>
            
            <div className="relative">
              {/* Timeline line */}
              <div className={`absolute left-6 top-0 bottom-0 w-1 ${
                theme === 'dark' ? 'bg-gradient-to-b from-emerald-500 via-blue-500 to-slate-600' : 'bg-gradient-to-b from-emerald-400 via-blue-400 to-slate-400'
              }`}></div>

              {/* Timeline items */}
              <div className="space-y-8">
                {recoveryMilestones.map((milestone, idx) => (
                  <div key={idx} className="relative pl-20">
                    {/* Timeline dot */}
                    <div className={`absolute left-0 top-2 w-14 h-14 rounded-full flex items-center justify-center border-4 ${
                      milestone.status === 'completed'
                        ? theme === 'dark'
                          ? 'bg-emerald-600 border-emerald-500'
                          : 'bg-emerald-500 border-emerald-400'
                        : milestone.status === 'in-progress'
                          ? theme === 'dark'
                            ? 'bg-blue-600 border-blue-500'
                            : 'bg-blue-500 border-blue-400'
                          : theme === 'dark'
                            ? 'bg-slate-700 border-slate-600'
                            : 'bg-slate-300 border-slate-400'
                    }`}>
                      {milestone.status === 'completed' ? '✅' : milestone.status === 'in-progress' ? '⏳' : '⭕'}
                    </div>

                    {/* Content */}
                    <div className={`rounded-lg p-6 border transition-all duration-300 hover:scale-105 ${
                      theme === 'dark'
                        ? 'bg-slate-700/30 border-slate-600/30 hover:border-slate-500/50'
                        : 'bg-slate-100/40 border-slate-300/30 hover:border-slate-300/60'
                    }`}>
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-bold text-lg">{milestone.milestone}</h4>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          milestone.status === 'completed'
                            ? theme === 'dark'
                              ? 'bg-emerald-500/20 text-emerald-300'
                              : 'bg-emerald-100 text-emerald-700'
                            : milestone.status === 'in-progress'
                              ? theme === 'dark'
                                ? 'bg-blue-500/20 text-blue-300'
                                : 'bg-blue-100 text-blue-700'
                              : theme === 'dark'
                                ? 'bg-slate-500/20 text-slate-300'
                                : 'bg-slate-200 text-slate-700'
                        }`}>
                          {milestone.status === 'completed' ? 'Completed' : milestone.status === 'in-progress' ? 'In Progress' : 'Upcoming'}
                        </span>
                      </div>
                      <p className={`text-sm mb-2 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                        Week {milestone.week} • {milestone.date}
                      </p>
                      <p className="font-medium">{milestone.details}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* INSIGHTS TAB */}
        {activeTab === 'insights' && (
          <div className="space-y-8">
            <div className={`rounded-2xl backdrop-blur border p-8 ${
              theme === 'dark'
                ? 'bg-slate-800/40 border-slate-700/50'
                : 'bg-white/40 border-slate-300/50'
            }`} style={{ animation: `slideUp 0.6s ease-out 0.1s both` }}>
              <h3 className="text-2xl font-bold mb-6">Key Insights & Patterns</h3>
              
              <div className="space-y-4">
                {[
                  { 
                    title: '🔍 Peak Effect Window',
                    insight: 'Days 3-4 post-treatment show the strongest side effects',
                    action: 'Schedule important activities outside this window'
                  },
                  {
                    title: '📈 Recovery Pattern',
                    insight: 'You show 85% improvement by day 7 of each cycle',
                    action: 'Plan recovery activities accordingly'
                  },
                  {
                    title: '💊 Medication Impact',
                    insight: 'Anti-nausea medication reduces nausea by 60% within 2 hours',
                    action: 'Take medication at first sign of nausea'
                  },
                  {
                    title: '⚡ Energy Correlation',
                    insight: 'Sleep quality directly impacts next-day energy levels',
                    action: 'Prioritize sleep hygiene on treatment days'
                  },
                  {
                    title: '🧠 Mental Health Trend',
                    insight: 'Anxiety peaks 24 hours pre-treatment (anticipatory)',
                    action: 'Schedule support sessions before treatment days'
                  },
                  {
                    title: '🍽️ Nutrition Impact',
                    insight: 'Small frequent meals reduce nausea vs 3 large meals',
                    action: 'Switch to 5-6 small meals daily'
                  },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className={`rounded-lg p-6 border transition-all duration-300 hover:scale-105 cursor-pointer ${
                      theme === 'dark'
                        ? 'bg-slate-700/30 border-slate-600/30 hover:border-slate-500/50'
                        : 'bg-slate-100/40 border-slate-300/30 hover:border-slate-300/60'
                    }`}
                  >
                    <h4 className="font-bold text-lg mb-2">{item.title}</h4>
                    <p className={`text-sm mb-3 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                      {item.insight}
                    </p>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                      <span className="text-sm font-semibold text-emerald-400">💡 Action: </span>
                      <span className="text-sm text-emerald-300">{item.action}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&display=swap');

        * {
          font-family: 'Outfit', sans-serif;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: transparent;
        }

        ::-webkit-scrollbar-thumb {
          background: rgba(16, 185, 129, 0.3);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: rgba(16, 185, 129, 0.6);
        }
      `}</style>
    </div>
  );
}

